import { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useIsAuthenticated } from 'react-auth-kit';
import { useCartContext } from 'context/CartContext/CartContext';
import { useAuthCode } from 'context/AuthCodeContext/AuthCodeContext';
import { calculatePrice } from 'api/checkoutpage';
import { PromoCodeStatus, CalculatePriceResponse } from 'api/responseTypes';
import { PROMO_CODE_STORAGE_KEY } from '../constants';
import { mapCartTicketsToCalculatePriceTickets } from '../utils/mapCartTicketsToCalculatePriceTickets';

type UsePromoCodeProps = {
  onPriceCalculated?: (response: CalculatePriceResponse) => void;
  onPromoCodeRemoved?: () => void;
  onAuthRequired?: () => void;
  priceCalculation?: CalculatePriceResponse | null;
};

export const usePromoCode = ({
  onPriceCalculated,
  onPromoCodeRemoved,
  onAuthRequired,
  priceCalculation,
}: UsePromoCodeProps = {}) => {
  const { formatMessage } = useIntl();
  const isAuthenticated = useIsAuthenticated();
  const { promoCode, setPromoCode, clearPromoCode, tickets, eventId } = useCartContext();
  const { authCode } = useAuthCode();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removingPromoCode, setRemovingPromoCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidatingForNewEvent, setIsValidatingForNewEvent] = useState(false);
  const previousEventId = useRef<number | undefined>(undefined);
  const validationStartedAt = useRef<number | null>(null);
  const previousPriceCalculation = useRef<CalculatePriceResponse | null | undefined>(undefined);

  useEffect(() => {
    if (promoCode) {
      localStorage.setItem(PROMO_CODE_STORAGE_KEY, promoCode);
    } else {
      localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
    }
  }, [promoCode]);

  useEffect(() => {
    const storedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
    const hasPromoCode = promoCode || storedPromoCode;

    if (
      previousEventId.current !== undefined &&
      previousEventId.current !== eventId &&
      hasPromoCode &&
      eventId
    ) {
      setIsValidatingForNewEvent(true);
      validationStartedAt.current = Date.now();
    }

    previousEventId.current = eventId;
  }, [eventId, promoCode]);

  useEffect(() => {
    if (isValidatingForNewEvent && validationStartedAt.current !== null) {
      if (
        priceCalculation !== null &&
        priceCalculation !== undefined &&
        previousPriceCalculation.current !== priceCalculation
      ) {
        setIsValidatingForNewEvent(false);
        validationStartedAt.current = null;
      }
    }
    previousPriceCalculation.current = priceCalculation;
  }, [priceCalculation, isValidatingForNewEvent]);

  const validatePromoCode = async (code: string): Promise<number | null> => {
    if (!code.trim()) {
      return null;
    }

    if (!eventId || tickets.length === 0) {
      setError(
        formatMessage({
          id: 'checkout.promoCode.error',
          defaultMessage: 'Failed to apply promo code. Please try again.',
        }),
      );
      return null;
    }

    const paymentMethod = authCode ? 'MINI_PROGRAM' : 'QI_SERVICES_PAYMENT_GATEWAY';
    const requestTickets = mapCartTicketsToCalculatePriceTickets(tickets);

    try {
      const response = await calculatePrice({
        eventId,
        tickets: requestTickets,
        paymentMethod,
        promoCode: code.trim(),
      });

      if (
        (response.promoCodeStatus === PromoCodeStatus.FULLY_VALID ||
          response.promoCodeStatus === PromoCodeStatus.PARTIALLY_VALID) &&
        response.appliedPromoCodesAmount > 0
      ) {
        localStorage.setItem(PROMO_CODE_STORAGE_KEY, code.trim());
        if (onPriceCalculated) {
          onPriceCalculated(response);
        }
        return response.appliedPromoCodesAmount;
      }

      setError(
        formatMessage({
          id: 'checkout.promoCode.invalid',
          defaultMessage: 'This promo code is invalid or has expired',
        }),
      );

      return null;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 && onAuthRequired && !isAuthenticated()) {
        onAuthRequired();
        return null;
      }

      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        formatMessage({
          id: 'checkout.promoCode.error',
          defaultMessage: 'Failed to apply promo code. Please try again.',
        });

      setError(errorMessage);
      return null;
    }
  };

  const handleApply = async () => {
    if (!inputValue.trim()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const discount = await validatePromoCode(inputValue.trim());

      if (discount && discount > 0) {
        setPromoCode(inputValue.trim(), discount);
        setInputValue('');
      } else {
        setError(
          formatMessage({
            id: 'checkout.promoCode.invalid',
            defaultMessage: 'This promo code is invalid or has expired',
          }),
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!promoCode) {
      return;
    }

    setIsRemoving(true);
    setRemovingPromoCode(promoCode);
    setError(null);

    try {
      if (onPromoCodeRemoved && eventId && tickets.length > 0) {
        await onPromoCodeRemoved();
      }

      clearPromoCode();
      localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
      setInputValue('');
    } catch (removeError) {
      setError(
        formatMessage({
          id: 'checkout.promoCode.error',
          defaultMessage: 'Failed to remove promo code. Please try again.',
        }),
      );
    } finally {
      setRemovingPromoCode(null);
      setIsRemoving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setInputValue(value);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim() && !isLoading) {
      handleApply();
    }
  };

  const displayPromoCode = isValidatingForNewEvent
    ? null
    : isRemoving && removingPromoCode
    ? removingPromoCode
    : promoCode;

  return {
    promoCode,
    displayPromoCode,
    inputValue,
    isLoading,
    isRemoving,
    isValidatingForNewEvent,
    error,
    handleApply,
    handleRemove,
    handleInputChange,
    handleKeyDown,
  };
};
